import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Table
} from 'react-bootstrap';
import Base from '../Base';
import {
    getSearchResults,
    resetSearch,
    setSearch
} from '../../slices/commonReducer';
import { TABLE_HEADERS } from "../../constants/common";
import Loader from './Loader';
import { FaArrowLeft } from 'react-icons/fa';
import { truncateText } from '../../utils';

const SearchPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const { searchList, loading } = useSelector(state => state.common);

    const [searchText, setSearchText] = useState("");
    const [searchType, setSearchType] = useState(1);
    useEffect(() => {
        if (searchText.trim() !== "" && searchList && Array.isArray(searchList)) {
            // Clear existing results first to prevent data mismatch
            dispatch(resetSearch()); // ← This is crucial!

            const data = {
                filter_type: searchType,
                q: searchText,
            };
            dispatch(getSearchResults(data));

            navigate(`/search?q=${encodeURIComponent(searchText)}&type=${searchType}`, { replace: true });
        }
    }, [searchType]);
    // Handle URL parameters and auto-search
    useEffect(() => {
        dispatch(resetSearch());

        // Get parameters from URL
        const queryParam = searchParams.get('q');
        const typeParam = searchParams.get('type');

        if (queryParam) {
            setSearchText(queryParam);
        }

        if (typeParam) {
            setSearchType(parseInt(typeParam));
        }

        // Auto-search if both parameters are present
        if (queryParam && typeParam) {
            const searchData = {
                filter_type: parseInt(typeParam),
                q: queryParam,
            };
            dispatch(getSearchResults(searchData));
        }

        return () => {
            dispatch(setSearch(false));
        };
    }, [dispatch, searchParams]);

    const searchData = () => {
        const data = {
            filter_type: searchType,
            q: searchText,
        };
        dispatch(getSearchResults(data));

        // Update URL with current search parameters
        navigate(`/search?q=${encodeURIComponent(searchText)}&type=${searchType}`, { replace: true });
    };

    const clearSearch = () => {
        setSearchText("");
        setSearchType(1);
        dispatch(resetSearch());

        // Clear URL parameters
        navigate('/search', { replace: true });
    };

    const handleBackToHome = () => {
        dispatch(setSearch(false));
        navigate(-1);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && searchText.trim() !== '') {
            searchData();
        }
    };

    return (
        <Base sticky_footer={false} hideDatepicker={true}>
            <Container fluid>
                <Row>
                    <Col>
                        <Card className="shadow-sm border-0 p-3 rounded-3 position-relative">
                            <Card.Header className="search-card-header pl-0">
                                <div className="mb-3">
                                    <Button
                                        variant="link"
                                        className="text-primary d-flex align-items-center gap-2 p-0 mb-2 transition-transform duration-200 hover:scale-105"
                                        onClick={handleBackToHome}
                                        style={{ fontSize: "16px", textDecoration: "none", background: "none", border: "none" }}
                                    >
                                        <FaArrowLeft size={14} /> Back
                                    </Button>

                                    <h5 className="text-2xl !font-semibold text-gray-800 mb-0">Search Criteria</h5>
                                </div>
                            </Card.Header>
                            <Form onSubmit={(e) => e.preventDefault()}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                    <Form.Group controlId="searchText" className="w-full">
                                        <Form.Label className="block !text-base !text-gray-700 mb-1">Search For</Form.Label>
                                        <Form.Control
                                            type="text"
                                            className="form-control input-focus-red focus:border-indigo-500 focus:ring-0"
                                            placeholder="Enter search term..."
                                            maxLength={100}
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                        />
                                    </Form.Group>

                                    <Form.Group controlId="searchType" className="w-full">
                                        <Form.Label className="block !text-base !text-gray-700 mb-1">Search Type</Form.Label>
                                        <Form.Select
                                            className="!text-base focus:outline-none focus:ring-0 focus:ring-indigo-500 focus:border-indigo-500"
                                            value={searchType}
                                            onChange={(e) => setSearchType(parseInt(e.target.value))}
                                        >
                                            <option value="1">Client Name</option>
                                            {/* <option value="2">GST Number</option>
                                            <option value="3">Invoice Number</option> */}
                                        </Form.Select>

                                    </Form.Group>
                                </div>
                                <div className="flex justify-end space-x-3">
                                    <Button
                                        variant="primary !bg-[#F37920]"
                                        className="fw-bold"
                                        disabled={searchText.trim() === ""}
                                        onClick={searchData}
                                        style={{ width: "100px" }}
                                    >
                                        Search
                                    </Button>
                                    <Button
                                        variant='outline-secondary no-hover'
                                        className="fw-bold"
                                        onClick={clearSearch}
                                        style={{ width: "100px" }}
                                    >
                                        Clear
                                    </Button>

                                </div>
                            </Form>
                        </Card>
                    </Col>
                </Row>
                {Array.isArray(searchList) && !!searchList.length && (
                    <Row className="mt-4">
                        <Card.Header
                            className="bg-gradient p-3"
                            style={{
                                background: 'linear-gradient(90deg,rgb(102, 139, 190),rgb(82, 109, 146))',
                                color: 'white',
                            }}
                        >
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 text-base rounded-full">
                                    {searchList.length} Result(s) Found
                                </span>
                            </div>
                        </Card.Header>
                        {loading ? (
                            <Loader />
                        ) : (
                            <Col>
                                <Card>


                                    <Card.Body className="p-0">
                                        <div className="table-responsive">
                                            <Table
                                                responsive
                                                className="core-ui-table"
                                            >
                                                <thead className="table-primary text-dark border-b border-gray-300 bg-gray-50">
                                                    <tr>
                                                        {TABLE_HEADERS[searchType].map((header) => (
                                                            <th
                                                                style={{
                                                                    // paddingLeft: '1.5rem',
                                                                    // paddingTop: '0.75rem',
                                                                    // paddingBottom: '0.75rem',
                                                                    textAlign: 'left',
                                                                    fontSize: '1.0rem',
                                                                    fontWeight: 600,
                                                                    textTransform: 'capitalize',
                                                                    // letterSpacing: '0.05em',
                                                                    // color: 'rgb(107, 114, 128)',
                                                                }}
                                                                key={header.key}
                                                            >
                                                                {header.label}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {searchList.map((data, index) => (
                                                        <tr key={`${data.id}-${index}`} className="bg-white !border-b px-4 py-3 align-top">
                                                            {TABLE_HEADERS[searchType].map((header) => {
                                                                const field_key = !!header.subkey;
                                                                const updatedData = {
                                                                    ...data,
                                                                    search_text: searchText,
                                                                };
                                                                let cellValue;

                                                                if (header.modifier) {
                                                                    cellValue = header.modifier(updatedData, () =>
                                                                        clearSearch()
                                                                    );
                                                                } else if (field_key) {
                                                                    const rawValue = data[header.key]?.[header.subkey] ?? 'N/A';
                                                                    cellValue = truncateText(rawValue, 25);
                                                                } else {
                                                                    const rawValue = data[header.key] ?? 'N/A';
                                                                    cellValue = truncateText(rawValue, 25);
                                                                }

                                                                return (
                                                                    <td
                                                                        key={`${data.id}-${header.key}`}
                                                                        className="text-left font-base text-gray-900"
                                                                    >
                                                                        {cellValue}
                                                                    </td>

                                                                );
                                                            })}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Card.Body>

                                </Card>
                            </Col>
                        )}
                    </Row>
                )}


                {/* No Results Section */}
                {Array.isArray(searchList) && !searchList.length && searchText && (
                    <Row className="mt-4">
                        <Col>
                            <Card className="shadow-md border-0 p-3 rounded-3 card">
                                <Card.Body className="text-center py-5">
                                    <div className="text-muted">
                                        <i className="fas fa-search fa-3x mb-3"></i>
                                        <h5>No Results Found</h5>
                                        <p>No results found for "{searchText}" 
                                            {/* in {
                                            searchType === 1 ? 'Client Name' 
                                            : searchType === 2 ? 'GST Number' : 'Invoice Number'
                                        } */}
                                        </p>
                                        <p>Try adjusting your search criteria or search type.</p>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Empty State - when no search has been performed */}
                {!Array.isArray(searchList) && !searchText && (
                    <Row className="mt-4">
                        <Col>
                            <Card>
                                <Card.Body className="text-center py-5">
                                    <div className="text-muted">
                                        <i className="fas fa-search fa-3x mb-3"></i>
                                        <h5>Start Your Search</h5>
                                        <p>Enter a search term above to find what you're looking for.</p>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}
            </Container>
        </Base>
    );
};

export default SearchPage;